import React from 'react';
import { Link } from 'react-router-dom';
import { werkgebiedPlaces, officeCoords } from '../data/werkgebied';
interface RegioMapProps {
  /** Slug van de plaats die uitgelicht wordt, bijvoorbeeld op een werkgebiedpagina. */
  activeSlug?: string;
  className?: string;
}

// Schematische kaart van Noord-Holland Noord. De kustlijn is vereenvoudigd
// en de plaatsen staan op benaderde coördinaten: een overzicht, geen
// routekaart.
const BOUNDS = {
  minLat: 52.5,
  maxLat: 52.99,
  minLng: 4.55,
  maxLng: 5.32
};
const WIDTH = 320;
// Lengtegraden zijn op deze breedte korter dan breedtegraden (cos 52,7).
const LNG_FACTOR = 0.606;
const HEIGHT = Math.round(WIDTH * (BOUNDS.maxLat - BOUNDS.minLat) / ((BOUNDS.maxLng - BOUNDS.minLng) * LNG_FACTOR));
// Pixels per kilometer, voor de afstandsringen rond ons kantoor.
const PX_PER_KM = HEIGHT / ((BOUNDS.maxLat - BOUNDS.minLat) * 111);
const RINGS_KM = [10, 25];

// Deze plaats valt samen met ons kantoor en krijgt daarom het kantoorpunt.
const OFFICE_SLUG = 'langedijk-en-oudkarspel';

// Aan welke kant het label van een plaats staat, zodat labels in het drukke
// gebied rond Alkmaar niet over elkaar vallen.
const LABEL_LEFT = new Set(['bergen', 'heiloo']);
function project(lat: number, lng: number) {
  return {
    x: (lng - BOUNDS.minLng) / (BOUNDS.maxLng - BOUNDS.minLng) * WIDTH,
    y: (BOUNDS.maxLat - lat) / (BOUNDS.maxLat - BOUNDS.minLat) * HEIGHT
  };
}
const toPercent = (p: {
  x: number;
  y: number;
}) => ({
  left: `${p.x / WIDTH * 100}%`,
  top: `${p.y / HEIGHT * 100}%`
});

// Vereenvoudigde omtrek van het land: Noordzeekust, Den Helder, de kust
// langs de Waddenzee en het IJsselmeer en Markermeer tot onder Hoorn.
const land = [[52.5, 4.58], [52.6, 4.615], [52.7, 4.64], [52.8, 4.665], [52.9, 4.7], [52.95, 4.725], [52.965, 4.755], [52.955, 4.8], [52.94, 4.9], [52.95, 5.0], [52.935, 5.04], [52.87, 5.08], [52.77, 5.11], [52.73, 5.22], [52.7, 5.29], [52.68, 5.26], [52.66, 5.15], [52.64, 5.07], [52.6, 5.06], [52.55, 5.07], [52.5, 5.05]].map(([lat, lng], i) => {
  const p = project(lat, lng);
  return `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
}).join(' ').concat(' Z');
const waterLabels = [{
  text: 'Waddenzee',
  x: 132,
  y: 14
}, {
  text: 'IJsselmeer',
  x: 244,
  y: 112
}, {
  text: 'Markermeer',
  x: 232,
  y: 292
}];
export const RegioMap: React.FC<RegioMapProps> = ({
  activeSlug,
  className = ''
}) => {
  const office = project(officeCoords.lat, officeCoords.lng);
  const places = werkgebiedPlaces.filter(place => place.slug !== OFFICE_SLUG);
  const officeActive = activeSlug === OFFICE_SLUG;
  return <figure className={className} data-id="element-685" data-mp="23u4ezs512qx4">
      <div className="relative" data-id="element-686" data-mp="23t2znbgzbi9l">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-auto w-full" role="img" aria-label="Schematische kaart van Noord-Holland Noord met ons kantoor in Oudkarspel en de plaatsen in ons werkgebied" data-id="element-687" data-mp="1ywmdgmtcbdz5">
          <defs data-id="element-688" data-mp="2rk8ilzi7uqo4">
            <clipPath id="regio-clip" data-id="element-689" data-mp="2rjp6x0j5syij">
              <rect width={WIDTH} height={HEIGHT} rx="14" data-id="element-690" data-mp="2rinno8rzp5pq" />
            </clipPath>
          </defs>
          <g clipPath="url(#regio-clip)" data-id="element-691" data-mp="2zvh8tn8r0iwd">
            {/* Water en land */}
            <rect width={WIDTH} height={HEIGHT} fill="#DCEBF7" data-id="element-692" data-mp="33vhtvqqcrsce" />
            <path d={land} fill="#FFFFFF" stroke="#B9CDE0" strokeWidth="1.25" strokeLinejoin="round" data-id="element-693" data-mp="38tt5a5waqawq" />

            {/* Afstandsringen vanaf kantoor */}
            {RINGS_KM.map(km => <g key={km} data-id="element-694" data-mp="3is8kbai1ttpv">
                <circle cx={office.x} cy={office.y} r={km * PX_PER_KM} fill="none" stroke="#004AC7" strokeOpacity="0.22" strokeDasharray="3 4" data-id="element-695" data-mp="3it1uklg7v1cr" />
                <text x={office.x} y={office.y - km * PX_PER_KM - 3} textAnchor="middle" fontSize="8.5" fontWeight="600" fill="#607697" data-id="element-696" data-mp="3mrr608hp28tr">
                  {km} km
                </text>
              </g>)}

            {/* Verbindingen naar de plaatsen */}
            {places.map(place => {
            const p = project(place.coords.lat, place.coords.lng);
            const isActive = place.slug === activeSlug;
            return <line key={place.slug} x1={office.x} y1={office.y} x2={p.x} y2={p.y} stroke="#004AC7" strokeOpacity={isActive ? 1 : 0.18} strokeWidth={isActive ? 2 : 1} data-id="element-697" data-mp="173u2936gmx6f" />;
          })}

            {/* Waternamen */}
            <text transform={`translate(20 ${HEIGHT * 0.42}) rotate(-90)`} textAnchor="middle" fontSize="10" fontWeight="500" letterSpacing="1.5" fill="#607697" data-id="element-698" data-mp="1gtdkq7q9gp0r">
              NOORDZEE
            </text>
            {waterLabels.map(label => <text key={label.text} x={label.x} y={label.y} fontSize="9.5" fontWeight="500" fill="#607697" data-id="element-699" data-mp="o60xrwg6uf6x">
                {label.text}
              </text>)}

            {/* Kantoor */}
            <circle cx={office.x} cy={office.y} r="11" fill="#004AC7" fillOpacity="0.15" data-id="element-700" data-mp="x5we0folp0dt" />
            <circle cx={office.x} cy={office.y} r="6" fill="#004AC7" stroke="#FFFFFF" strokeWidth={officeActive ? 3 : 2} data-id="element-701" data-mp="t3j9tvpi5aj5" />
          </g>
        </svg>

        {/* Plaatsen als echte links over de kaart heen */}
        <ul className="absolute inset-0" data-id="element-702" data-mp="24jzkzcu0x507">
          {places.map(place => {
          const p = project(place.coords.lat, place.coords.lng);
          const isActive = place.slug === activeSlug;
          const labelLeft = LABEL_LEFT.has(place.slug);
          return <li key={place.slug} className="absolute" style={toPercent(p)} data-id="element-703" data-mp="2gzh423xyk4p1">
                <Link to={`/werkgebied/${place.slug}`} aria-current={isActive ? 'page' : undefined} className="group absolute block h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#004AC7]" data-id="element-704" data-mp="226dt4fevgp7a">
                  <span className={`absolute inset-1 rounded-full border-2 border-[#004AC7] transition-colors duration-150 ${isActive ? 'bg-[#004AC7]' : 'bg-white group-hover:bg-[#004AC7]'}`} aria-hidden="true" data-id="element-705" data-mp="2zr9vhtxdphun" />
                  <span className={`absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] leading-none shadow-sm transition-colors duration-150 ${labelLeft ? 'right-5' : 'left-5'} ${isActive ? 'bg-[#004AC7] font-semibold text-white' : 'bg-white font-medium text-[#1F222C] group-hover:text-[#004AC7]'}`} data-id="element-706" data-mp="2q0xcw794jyr5">
                    {place.shortName}
                  </span>
                </Link>
              </li>;
        })}

          <li className="absolute" style={toPercent(office)} data-id="element-707" data-mp="3jduwnocuvadf">
            <Link to={`/werkgebied/${OFFICE_SLUG}`} aria-current={officeActive ? 'page' : undefined} className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#004AC7] px-2 py-1 text-[11px] font-semibold leading-none text-white shadow-sm transition-colors duration-150 hover:bg-[#003A9E]" data-id="element-708" data-mp="3jca4u6hxe5j9">
              Oudkarspel, ons kantoor
            </Link>
          </li>
        </ul>
      </div>

      {/* Legenda */}
      <figcaption className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[#607697]" data-id="element-709" data-mp="2voshcn5p1tm3">
        <span className="inline-flex items-center gap-1.5" data-id="element-710" data-mp="2qp64iets2fdt">
          <span className="h-2.5 w-2.5 rounded-full bg-[#004AC7]" aria-hidden="true" data-id="element-711" data-mp="2qoo373s9daz1" />
          Ons kantoor
        </span>
        <span className="inline-flex items-center gap-1.5" data-id="element-712" data-mp="118amaq4robp9">
          <span className="h-2.5 w-2.5 rounded-full border-2 border-[#004AC7] bg-white" aria-hidden="true" data-id="element-713" data-mp="117seqxcnmb21" />
          Plaats met eigen pagina
        </span>
        <span className="inline-flex items-center gap-1.5" data-id="element-714" data-mp="1b5yhtia75n8h">
          <span className="w-4 border-t border-dashed border-[#004AC7]/50" aria-hidden="true" data-id="element-715" data-mp="1b4dkjkwhzq6x" />
          Hemelsbreed vanaf kantoor
        </span>
      </figcaption>
    </figure>;
};